import React, { useState, useEffect, useRef } from "react";
import { Routes, Route, Link, useLocation } from "react-router-dom";
import axios from "../utils/axios";
import toast from "react-hot-toast";
import MenuAccessManagement from "../components/MenuAccessManagement";
import usePagination from "../hooks/usePagination";
import Pagination from "../components/Pagination";

const AdminDashboard = () => {
  const location = useLocation();
  const isActive = (path) => location.pathname.includes(path);
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex">
        {/* Sidebar */}
        <div className="w-64 bg-white shadow-sm">
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900">Admin Dashboard</h2>
          </div>
          <nav className="mt-6">
            <div className="px-6 space-y-1">
              <Link to="/admin" className={`block px-3 py-2 rounded-md text-sm font-medium ${location.pathname === "/admin" ? "bg-primary-100 text-primary-700" : "text-gray-700 hover:bg-gray-100"}`}>📊 Overview</Link>
              <Link to="/admin/leads" className={`block px-3 py-2 rounded-md text-sm font-medium ${isActive("/admin/leads") ? "bg-primary-100 text-primary-700" : "text-gray-700 hover:bg-gray-100"}`}>👥 Leads</Link>
              <Link to="/admin/appointments" className={`block px-3 py-2 rounded-md text-sm font-medium ${isActive("/admin/appointments") ? "bg-primary-100 text-primary-700" : "text-gray-700 hover:bg-gray-100"}`}>📅 Appointments</Link>
              <Link to="/admin/orders" className={`block px-3 py-2 rounded-md text-sm font-medium ${isActive("/admin/orders") ? "bg-primary-100 text-primary-700" : "text-gray-700 hover:bg-gray-100"}`}>🛒 Orders</Link>
              <Link to="/admin/products" className={`block px-3 py-2 rounded-md text-sm font-medium ${isActive("/admin/products") ? "bg-primary-100 text-primary-700" : "text-gray-700 hover:bg-gray-100"}`}>📦 Products</Link>
              <Link to="/admin/communications" className={`block px-3 py-2 rounded-md text-sm font-medium ${isActive("/admin/communications") ? "bg-primary-100 text-primary-700" : "text-gray-700 hover:bg-gray-100"}`}>💬 Communications</Link>
              <Link to="/admin/tasks" className={`block px-3 py-2 rounded-md text-sm font-medium ${isActive("/admin/tasks") ? "bg-primary-100 text-primary-700" : "text-gray-700 hover:bg-gray-100"}`}>✅ All User Tasks</Link>
              <Link to="/admin/menu-access" className={`block px-3 py-2 rounded-md text-sm font-medium ${isActive("/admin/menu-access") ? "bg-primary-100 text-primary-700" : "text-gray-700 hover:bg-gray-100"}`}>🔒 Menu Access Control</Link>
              <Link to="/admin/cart-events" className={`block px-3 py-2 rounded-md text-sm font-medium ${isActive("/admin/cart-events") ? "bg-primary-100 text-primary-700" : "text-gray-700 hover:bg-gray-100"}`}>🛒 Add-to-Cart List</Link>
            </div>
          </nav>
        </div>
        {/* Main Content */}
        <div className="flex-1 p-8">
          <Routes>
            <Route path="/" element={<AdminOverview />} />
            <Route path="/leads" element={<AdminLeads />} />
            <Route path="/appointments" element={<AdminAppointments />} />
            <Route path="/orders" element={<AdminOrders />} />
            <Route path="/products" element={<AdminProducts />} />
            <Route path="/communications" element={<AdminCommunications />} />
            <Route path="/tasks" element={<AdminTasks />} />
            <Route path="/menu-access" element={<MenuAccessManagement />} />
            <Route path="/cart-events" element={<AdminCartEvents />} />
          </Routes>
        </div>
      </div>
    </div>
  );
};

// Admin Add-to-Cart List Component (only at end)
function AdminCartEvents() {
  const [cartEvents, setCartEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const formatAddedAt = (timestamp) => {
    if (!timestamp) return "-";

    const value = String(timestamp);
    const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/.test(value);
    const date = new Date(
      hasTimezone ? value : `${value.replace(" ", "T")}Z`
    );

    if (Number.isNaN(date.getTime())) return "-";

    return new Intl.DateTimeFormat("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
      timeZoneName: "short",
    }).format(date);
  };

  useEffect(() => {
    const fetchCartEvents = async () => {
      setLoading(true);
      try {
        const res = await axios.get("/api/cart-events");
        setCartEvents(res.data);
      } catch (err) {
        console.error("Error fetching cart events:", err);
        toast.error("Failed to load add-to-cart activity");
        setCartEvents([]);
      } finally {
        setLoading(false);
      }
    };
    fetchCartEvents();
  }, []);

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Add-to-Cart List</h1>
      {loading ? (
        <div className="text-center py-8">Loading...</div>
      ) : cartEvents.length === 0 ? (
        <div className="text-center py-8 text-gray-500">No add-to-cart activity found.</div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Added At (IST)</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {cartEvents.map((event) => (
                <tr key={event.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{event.user_name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{event.email}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{event.product_name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{event.quantity}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatAddedAt(event.added_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Admin Overview Component
const AdminOverview = () => {
  const location = useLocation();
  const [stats, setStats] = useState({
    leads: { total_leads: 0 },
    appointments: { total_appointments: 0 },
    orders: { total_orders: 0, total_revenue: 0 },
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchOverviewStats = async () => {
    try {
      // Add timestamp to prevent caching
      const response = await axios.get("/api/analytics/overview", {
        params: { _t: Date.now() },
      });
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching overview stats:", error);
      toast.error("Failed to load dashboard statistics");
    }
  };

  const fetchRecentActivity = async () => {
    try {
      // Add timestamp to prevent caching
      const timestamp = Date.now();
      // Fetch recent leads, orders, and appointments
      const [leadsRes, ordersRes, appointmentsRes] = await Promise.all([
        axios.get("/api/leads", { params: { limit: 3, _t: timestamp } }),
        axios.get("/api/orders", { params: { limit: 3, _t: timestamp } }),
        axios.get("/api/appointments", { params: { limit: 3, _t: timestamp } }),
      ]);

      const activities = [];

      // Add recent leads
      if (leadsRes.data.leads) {
        leadsRes.data.leads.slice(0, 3).forEach((lead) => {
          activities.push({
            type: "lead",
            message: `New lead registered: ${lead.name}`,
            timestamp: lead.created_at,
            color: "bg-green-500",
          });
        });
      }

      // Add recent orders
      if (ordersRes.data.orders) {
        ordersRes.data.orders.slice(0, 3).forEach((order) => {
          const customerName = order.customer_name || "Customer";
          activities.push({
            type: "order",
            message: `Order placed by: ${customerName}`,
            timestamp: order.created_at,
            color: "bg-purple-500",
          });
        });
      }

      // Add recent appointments
      if (appointmentsRes.data.appointments) {
        appointmentsRes.data.appointments.slice(0, 3).forEach((appointment) => {
          const leadName = appointment.lead_name || "Customer";
          activities.push({
            type: "appointment",
            message: `Appointment booked: ${leadName}`,
            timestamp: appointment.created_at || appointment.scheduled_at,
            color: "bg-blue-500",
          });
        });
      }

      // Sort by timestamp (most recent first) and take top 5
      activities.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      setRecentActivity(activities.slice(0, 5));
    } catch (error) {
      console.error("Error fetching recent activity:", error);
      // Keep empty array if fetching fails
      setRecentActivity([]);
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    setLoading(true);
    await fetchOverviewStats();
    await fetchRecentActivity();
  };

  // Fetch data whenever we navigate to this page
  useEffect(() => {
    setLoading(true);
    const loadData = async () => {
      await fetchOverviewStats();
      await fetchRecentActivity();
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]); // Re-fetch when pathname changes

  const formatCurrency = (amount) => {
    if (!amount) return "₹0";
    return `₹${amount.toLocaleString("en-IN")}`;
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return "";
    const now = new Date();
    const time = new Date(timestamp);
    const diffInMinutes = Math.floor((now - time) / (1000 * 60));

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} days ago`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading dashboard statistics...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard Overview</h1>
        <button
          onClick={refreshData}
          disabled={loading}
          className="btn-secondary btn-sm flex items-center gap-2"
        >
          <span className={loading ? "animate-spin" : ""}>🔄</span>
          {loading ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-100 rounded-md flex items-center justify-center">
                <span className="text-blue-600 text-sm">👥</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Leads</p>
              <p className="text-2xl font-semibold text-gray-900">
                {stats.leads?.total_leads || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-green-100 rounded-md flex items-center justify-center">
                <span className="text-green-600 text-sm">📅</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Appointments</p>
              <p className="text-2xl font-semibold text-gray-900">
                {stats.appointments?.total_appointments || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-purple-100 rounded-md flex items-center justify-center">
                <span className="text-purple-600 text-sm">🛒</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Orders</p>
              <p className="text-2xl font-semibold text-gray-900">
                {stats.orders?.total_orders || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-yellow-100 rounded-md flex items-center justify-center">
                <span className="text-yellow-600 text-sm">💰</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Revenue</p>
              <p className="text-2xl font-semibold text-gray-900">
                {formatCurrency(stats.orders?.total_revenue)}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Recent Activity
        </h2>
        <div className="space-y-4">
          {recentActivity.length > 0 ? (
            recentActivity.map((activity, index) => (
              <div key={index} className="flex items-center">
                <div
                  className={`w-2 h-2 ${activity.color} rounded-full mr-3`}
                ></div>
                <span className="text-sm text-gray-600">
                  {activity.message}
                </span>
                <span className="ml-auto text-xs text-gray-400">
                  {formatTimeAgo(activity.timestamp)}
                </span>
              </div>
            ))
          ) : (
            <div className="text-sm text-gray-500 text-center py-4">
              No recent activity found
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Admin Leads Component - Full CRM functionality
const AdminLeads = () => {
  const [selectedLead, setSelectedLead] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [localFilters, setLocalFilters] = useState({
    status: "",
    source: "",
  });
  const isInitialMount = useRef(true);

  // Use pagination hook
  const {
    data: leads,
    pagination,
    loading,
    setPage,
    setLimit,
    setSearch,
    setFilters,
    searchValue,
    refresh,
  } = usePagination("/api/leads", {
    initialFilters: {},
    autoFetch: true,
  });

  // Update filters when local filters change
  useEffect(() => {
    // Skip on initial mount to prevent double fetch
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const filters = {};
    if (localFilters.status) filters.status = localFilters.status;
    if (localFilters.source) filters.source = localFilters.source;
    setFilters(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localFilters.status, localFilters.source]);

  const updateLeadStatus = async (leadId, newStatus, notes = "") => {
    try {
      await axios.patch(`/api/leads/${leadId}/status`, {
        status: newStatus,
        notes,
      });
      toast.success("Lead status updated successfully");
      refresh();
      if (selectedLead && selectedLead.id === leadId) {
        setSelectedLead({ ...selectedLead, status: newStatus, notes });
      }
    } catch (error) {
      console.error("Error updating lead status:", error);
      toast.error("Failed to update lead status");
    }
  };

  const viewLeadDetails = async (leadId) => {
    try {
      const response = await axios.get(`/api/leads/${leadId}`);
      setSelectedLead(response.data);
      setShowDetailModal(true);
    } catch (error) {
      console.error("Error fetching lead details:", error);
      toast.error("Failed to load lead details");
    }
  };

  const deleteLead = async (leadId) => {
    if (!window.confirm("Are you sure you want to delete this lead?")) {
      return;
    }
    try {
      await axios.delete(`/api/leads/${leadId}`);
      toast.success("Lead deleted successfully");
      refresh();
    } catch (error) {
      console.error("Error deleting lead:", error);
      toast.error("Failed to delete lead");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "new":
        return "bg-blue-100 text-blue-800";
      case "contacted":
        return "bg-yellow-100 text-yellow-800";
      case "qualified":
        return "bg-purple-100 text-purple-800";
      case "converted":
        return "bg-green-100 text-green-800";
      case "lost":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Leads Management</h1>
        <button
          onClick={refresh}
          className="btn-secondary btn-md flex items-center gap-2"
        >
          <span>🔄</span> Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-gray-500">
          <p className="text-sm text-gray-600">Total Leads</p>
          <p className="text-2xl font-bold text-gray-900">
            {pagination?.total || 0}
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-blue-500">
          <p className="text-sm text-gray-600">New</p>
          <p className="text-2xl font-bold text-blue-600">
            {leads.filter((l) => l.status === "new").length}
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-yellow-500">
          <p className="text-sm text-gray-600">Contacted</p>
          <p className="text-2xl font-bold text-yellow-600">
            {leads.filter((l) => l.status === "contacted").length}
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-purple-500">
          <p className="text-sm text-gray-600">Qualified</p>
          <p className="text-2xl font-bold text-purple-600">
            {leads.filter((l) => l.status === "qualified").length}
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-green-500">
          <p className="text-sm text-gray-600">Converted</p>
          <p className="text-2xl font-bold text-green-600">
            {leads.filter((l) => l.status === "converted").length}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={localFilters.status}
              onChange={(e) =>
                setLocalFilters({ ...localFilters, status: e.target.value })
              }
              className="input"
            >
              <option value="">All Statuses</option>
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="qualified">Qualified</option>
              <option value="converted">Converted</option>
              <option value="lost">Lost</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Source
            </label>
            <select
              value={localFilters.source}
              onChange={(e) =>
                setLocalFilters({ ...localFilters, source: e.target.value })
              }
              className="input"
            >
              <option value="">All Sources</option>
              <option value="website">Website</option>
              <option value="social_media">Social Media</option>
              <option value="referral">Referral</option>
              <option value="campaign">Campaign</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => setFilters({ status: "", source: "", search: "" })}
              className="btn-secondary btn-md w-full"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Leads Table */}
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="loading-dots">
            <div></div>
            <div></div>
            <div></div>
            <div></div>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Lead Info
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Source
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {leads.map((lead) => (
                  <tr key={lead.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {lead.name}
                      </div>
                      <div className="text-sm text-gray-500">ID: {lead.id}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{lead.email}</div>
                      <div className="text-sm text-gray-500">{lead.phone}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {lead.source || "N/A"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <select
                        value={lead.status}
                        onChange={(e) =>
                          updateLeadStatus(lead.id, e.target.value)
                        }
                        className={`text-xs font-semibold rounded-full px-3 py-1 ${getStatusColor(
                          lead.status
                        )}`}
                      >
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="qualified">Qualified</option>
                        <option value="converted">Converted</option>
                        <option value="lost">Lost</option>
                      </select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(lead.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                      <button
                        onClick={() => viewLeadDetails(lead.id)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        View
                      </button>
                      <button
                        onClick={() => deleteLead(lead.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination Component */}
          <div className="px-6 py-4 border-t border-gray-200">
            <Pagination
              pagination={pagination}
              onPageChange={setPage}
              onLimitChange={setLimit}
              onSearchChange={setSearch}
              searchValue={searchValue}
              searchPlaceholder="Search by name, email, phone..."
              showSearch={true}
              showLimitSelector={true}
              limitOptions={[10, 20, 50, 100]}
            />
          </div>
        </div>
      )}

      {/* Lead Detail Modal */}
      {showDetailModal && selectedLead && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">
                  Lead Details
                </h2>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="text-2xl">×</span>
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* Lead Information */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-4">
                  Contact Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-600">Name</label>
                    <p className="text-base font-medium">
                      {selectedLead.lead.name}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Email</label>
                    <p className="text-base font-medium">
                      {selectedLead.lead.email}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Phone</label>
                    <p className="text-base font-medium">
                      {selectedLead.lead.phone}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">WhatsApp</label>
                    <p className="text-base font-medium">
                      {selectedLead.lead.whatsapp}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Source</label>
                    <p className="text-base font-medium">
                      {selectedLead.lead.source}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Status</label>
                    <p
                      className={`inline-block text-xs font-semibold rounded-full px-3 py-1 ${getStatusColor(
                        selectedLead.lead.status
                      )}`}
                    >
                      {selectedLead.lead.status.toUpperCase()}
                    </p>
                  </div>
                </div>
              </div>

              {/* Notes */}
              {selectedLead.lead.notes && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-2">Notes</h3>
                  <p className="text-gray-700 bg-gray-50 p-3 rounded">
                    {selectedLead.lead.notes}
                  </p>
                </div>
              )}

              {/* Appointments */}
              {selectedLead.appointments &&
                selectedLead.appointments.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold mb-2">
                      Appointments ({selectedLead.appointments.length})
                    </h3>
                    <div className="space-y-2">
                      {selectedLead.appointments.map((apt) => (
                        <div
                          key={apt.id}
                          className="bg-gray-50 p-3 rounded flex justify-between items-center"
                        >
                          <div>
                            <p className="font-medium">
                              {new Date(apt.scheduled_at).toLocaleString()}
                            </p>
                            <p className="text-sm text-gray-600">
                              Status: {apt.status}
                            </p>
                          </div>
                          {apt.meeting_link && (
                            <a
                              href={apt.meeting_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 text-sm"
                            >
                              Join Meeting
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              {/* Communications */}
              {selectedLead.communications &&
                selectedLead.communications.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold mb-2">
                      Communications ({selectedLead.communications.length})
                    </h3>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {selectedLead.communications.map((comm) => (
                        <div key={comm.id} className="bg-gray-50 p-3 rounded">
                          <div className="flex justify-between items-start mb-1">
                            <span className="text-xs font-semibold text-gray-600">
                              {comm.type.toUpperCase()} -{" "}
                              {comm.template_type.replace("_", " ")}
                            </span>
                            <span
                              className={`text-xs px-2 py-1 rounded ${
                                comm.status === "sent"
                                  ? "bg-green-100 text-green-800"
                                  : comm.status === "failed"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-yellow-100 text-yellow-800"
                              }`}
                            >
                              {comm.status}
                            </span>
                          </div>
                          <p className="text-sm text-gray-700 line-clamp-2">
                            {comm.content}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {new Date(comm.created_at).toLocaleString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowDetailModal(false)}
                className="btn-secondary btn-md"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Admin Appointments Component - Calendar and appointment management
const AdminAppointments = () => {
  const [syncing, setSyncing] = useState(false);
  const [viewMode, setViewMode] = useState("list"); // 'list' or 'calendar'
  const [localFilters, setLocalFilters] = useState({
    status: "",
    date: "",
  });
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [calendlyEvents, setCalendlyEvents] = useState([]);
  const isInitialMount = useRef(true);

  // Use pagination hook
  const {
    data: appointments,
    pagination,
    loading,
    setPage,
    setLimit,
    setSearch,
    setFilters,
    searchValue,
    refresh,
  } = usePagination("/api/appointments", {
    initialFilters: {},
    autoFetch: true,
  });

  // Update filters when local filters change
  useEffect(() => {
    // Skip on initial mount to prevent double fetch
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const filters = {};
    if (localFilters.status) filters.status = localFilters.status;
    if (localFilters.date) filters.date = localFilters.date;
    setFilters(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localFilters.status, localFilters.date]);

  useEffect(() => {
    fetchCalendlyEvents();
  }, []);

  const fetchCalendlyEvents = async () => {
    try {
      // Fetch real Calendly events for the next 30 days
      const today = new Date();
      const futureDate = new Date();
      futureDate.setDate(today.getDate() + 30);

      const response = await axios.get("/api/appointments/scheduled-events", {
        params: {
          start_date: today.toISOString(),
          end_date: futureDate.toISOString(),
        },
      });

      setCalendlyEvents(response.data.scheduledEvents || []);
      console.log(
        `Fetched ${
          response.data.scheduledEvents?.length || 0
        } Calendly events from API`
      );
    } catch (error) {
      console.error("Error fetching Calendly events:", error);
      // Don't show error to user - Calendly integration is optional
    }
  };

  const syncCalendlyEvents = async () => {
    setSyncing(true);
    try {
      await fetchCalendlyEvents();
      refresh();
      toast.success("Synced with Calendly successfully!");
    } catch (error) {
      console.error("Error syncing Calendly events:", error);
      toast.error("Failed to sync with Calendly");
    } finally {
      setSyncing(false);
    }
  };

  const updateAppointmentStatus = async (
    appointmentId,
    newStatus,
    notes = ""
  ) => {
    try {
      await axios.patch(`/api/appointments/${appointmentId}/status`, {
        status: newStatus,
        notes,
      });
      toast.success("Appointment status updated successfully");
      refresh();
      if (selectedAppointment && selectedAppointment.id === appointmentId) {
        setSelectedAppointment({
          ...selectedAppointment,
          status: newStatus,
          notes,
        });
      }
    } catch (error) {
      console.error("Error updating appointment status:", error);
      toast.error("Failed to update appointment status");
    }
  };

  const viewAppointmentDetails = (appointment) => {
    setSelectedAppointment(appointment);
    setShowDetailModal(true);
  };

  const cancelAppointment = async (appointmentId) => {
    if (!window.confirm("Are you sure you want to cancel this appointment?")) {
      return;
    }
    try {
      await axios.delete(`/api/appointments/${appointmentId}`);
      toast.success("Appointment cancelled successfully");
      refresh();
      setShowDetailModal(false);
    } catch (error) {
      console.error("Error cancelling appointment:", error);
      toast.error("Failed to cancel appointment");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "scheduled":
        return "bg-blue-100 text-blue-800";
      case "completed":
        return "bg-green-100 text-green-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      case "no_show":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const isCalendlyPending = (appointment) => {
    return (
      appointment.calendly_event_id &&
      appointment.calendly_event_id.startsWith("calendly_booking_")
    );
  };

  const groupAppointmentsByDate = () => {
    const grouped = {};
    appointments.forEach((apt) => {
      const date = apt.scheduled_at
        ? apt.scheduled_at.split("T")[0]
        : "No Date";
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(apt);
    });
    return grouped;
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          Appointments Management
        </h1>
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode("list")}
            className={`px-4 py-2 rounded ${
              viewMode === "list"
                ? "bg-primary-600 text-white"
                : "bg-gray-200 text-gray-700"
            }`}
          >
            📋 List
          </button>
          <button
            onClick={() => setViewMode("calendar")}
            className={`px-4 py-2 rounded ${
              viewMode === "calendar"
                ? "bg-primary-600 text-white"
                : "bg-gray-200 text-gray-700"
            }`}
          >
            📅 Calendar
          </button>
          <button
            onClick={syncCalendlyEvents}
            disabled={syncing}
            className="btn-secondary btn-md flex items-center gap-2"
          >
            {syncing ? (
              <>
                <div className="loading-dots">
                  <div></div>
                  <div></div>
                  <div></div>
                </div>
                Syncing...
              </>
            ) : (
              <>
                <span>🔄</span> Sync Calendly
              </>
            )}
          </button>
          <button onClick={refresh} className="btn-secondary btn-md">
            🔄 Refresh DB
          </button>
        </div>
      </div>

      {/* Info Banner */}
      {calendlyEvents.length > 0 && (
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 rounded">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-blue-500"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                <span className="font-semibold">
                  {calendlyEvents.length} Calendly event(s)
                </span>{" "}
                synced from your Calendly account. Appointments with "⚠️ Pending
                Confirmation" were initiated but not yet booked via Calendly.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-gray-500">
          <p className="text-sm text-gray-600">Total</p>
          <p className="text-2xl font-bold text-gray-900">
            {pagination?.total || 0}
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-blue-500">
          <p className="text-sm text-gray-600">Scheduled</p>
          <p className="text-2xl font-bold text-blue-600">
            {appointments.filter((a) => a.status === "scheduled").length}
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-yellow-500">
          <p className="text-sm text-gray-600">Pending</p>
          <p className="text-2xl font-bold text-yellow-600">
            {
              appointments.filter(
                (a) =>
                  a.calendly_event_id?.startsWith("calendly_booking_") &&
                  a.status === "scheduled"
              ).length
            }
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-green-500">
          <p className="text-sm text-gray-600">Completed</p>
          <p className="text-2xl font-bold text-green-600">
            {appointments.filter((a) => a.status === "completed").length}
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-red-500">
          <p className="text-sm text-gray-600">Cancelled</p>
          <p className="text-2xl font-bold text-red-600">
            {appointments.filter((a) => a.status === "cancelled").length}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={localFilters.status}
              onChange={(e) =>
                setLocalFilters({ ...localFilters, status: e.target.value })
              }
              className="input"
            >
              <option value="">All Statuses</option>
              <option value="scheduled">Scheduled</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="no_show">No Show</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date
            </label>
            <input
              type="date"
              value={localFilters.date}
              onChange={(e) =>
                setLocalFilters({ ...localFilters, date: e.target.value })
              }
              className="input"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={() => setLocalFilters({ status: "", date: "" })}
              className="btn-secondary btn-md w-full"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* List View */}
      {viewMode === "list" && (
        <>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="loading-dots">
                <div></div>
                <div></div>
                <div></div>
                <div></div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              {appointments.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  No appointments found matching the filters.
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Lead Info
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Scheduled Time
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Duration
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Confirmation
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Meeting Link
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {appointments.map((appointment) => (
                      <tr
                        key={appointment.id}
                        className={`hover:bg-gray-50 ${
                          isCalendlyPending(appointment) ? "bg-yellow-50" : ""
                        }`}
                      >
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">
                            {appointment.lead_name || "N/A"}
                          </div>
                          <div className="text-sm text-gray-500">
                            {appointment.lead_email}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {appointment.scheduled_at
                              ? new Date(
                                  appointment.scheduled_at
                                ).toLocaleDateString()
                              : "N/A"}
                          </div>
                          <div className="text-sm text-gray-500">
                            {appointment.scheduled_at
                              ? new Date(
                                  appointment.scheduled_at
                                ).toLocaleTimeString([], {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })
                              : ""}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {appointment.duration || 30} min
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <select
                            value={appointment.status}
                            onChange={(e) =>
                              updateAppointmentStatus(
                                appointment.id,
                                e.target.value
                              )
                            }
                            className={`text-xs font-semibold rounded-full px-3 py-1 ${getStatusColor(
                              appointment.status
                            )}`}
                          >
                            <option value="scheduled">Scheduled</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                            <option value="no_show">No Show</option>
                          </select>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {isCalendlyPending(appointment) ? (
                            <span className="text-xs font-semibold text-yellow-700 bg-yellow-100 px-2 py-1 rounded-full">
                              ⚠️ Pending
                            </span>
                          ) : (
                            <span className="text-xs font-semibold text-green-700 bg-green-100 px-2 py-1 rounded-full">
                              ✓ Confirmed
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {appointment.meeting_link ? (
                            <a
                              href={appointment.meeting_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-900"
                            >
                              {isCalendlyPending(appointment)
                                ? "Complete Booking"
                                : "Join"}
                            </a>
                          ) : (
                            <span className="text-gray-400">N/A</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                          <button
                            onClick={() => viewAppointmentDetails(appointment)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            View
                          </button>
                          <button
                            onClick={() => cancelAppointment(appointment.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Cancel
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* Pagination Component */}
              <div className="px-6 py-4 border-t border-gray-200">
                <Pagination
                  pagination={pagination}
                  onPageChange={setPage}
                  onLimitChange={setLimit}
                  onSearchChange={setSearch}
                  searchValue={searchValue}
                  searchPlaceholder="Search by lead name, email, phone..."
                  showSearch={true}
                  showLimitSelector={true}
                  limitOptions={[10, 20, 50, 100]}
                />
              </div>
            </div>
          )}
        </>
      )}

      {/* Calendar View */}
      {viewMode === "calendar" && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="loading-dots">
                <div></div>
                <div></div>
                <div></div>
                <div></div>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupAppointmentsByDate())
                .sort(([dateA], [dateB]) => dateA.localeCompare(dateB))
                .map(([date, dayAppointments]) => (
                  <div key={date} className="border-l-4 border-blue-500 pl-4">
                    <h3 className="text-lg font-semibold mb-3">
                      {date === "No Date"
                        ? "No Date"
                        : new Date(date + "T00:00:00").toLocaleDateString(
                            "en-US",
                            {
                              weekday: "long",
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            }
                          )}
                    </h3>
                    <div className="space-y-2">
                      {dayAppointments.map((apt) => (
                        <div
                          key={apt.id}
                          className={`p-4 rounded-lg flex justify-between items-center hover:bg-gray-100 cursor-pointer ${
                            isCalendlyPending(apt)
                              ? "bg-yellow-50 border-l-4 border-yellow-400"
                              : "bg-gray-50"
                          }`}
                          onClick={() => viewAppointmentDetails(apt)}
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-3 flex-wrap">
                              <span className="text-sm font-semibold text-gray-700">
                                {apt.scheduled_at
                                  ? new Date(
                                      apt.scheduled_at
                                    ).toLocaleTimeString([], {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    })
                                  : "Time TBD"}
                              </span>
                              <span className="text-sm font-medium text-gray-900">
                                {apt.lead_name || "Unknown Lead"}
                              </span>
                              <span
                                className={`text-xs font-semibold rounded-full px-2 py-1 ${getStatusColor(
                                  apt.status
                                )}`}
                              >
                                {apt.status}
                              </span>
                              {isCalendlyPending(apt) && (
                                <span className="text-xs font-semibold text-yellow-700 bg-yellow-100 px-2 py-1 rounded-full">
                                  ⚠️ Pending Calendly
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-600 mt-1">
                              {apt.lead_email} • {apt.lead_phone || "No phone"}
                            </div>
                          </div>
                          {apt.meeting_link && (
                            <a
                              href={apt.meeting_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="ml-4 text-blue-600 hover:text-blue-900 text-sm font-medium"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {isCalendlyPending(apt) ? "Complete →" : "Join →"}
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              {Object.keys(groupAppointmentsByDate()).length === 0 && (
                <div className="text-center text-gray-500 py-8">
                  No appointments to display
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Appointment Detail Modal */}
      {showDetailModal && selectedAppointment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">
                  Appointment Details
                </h2>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="text-2xl">×</span>
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* Pending Calendly Warning */}
              {isCalendlyPending(selectedAppointment) && (
                <div className="mb-6 bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-yellow-400"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-yellow-700">
                        <strong>Pending Calendly Confirmation</strong>
                      </p>
                      <p className="mt-1 text-sm text-yellow-700">
                        This appointment was initiated in the system but has not
                        been confirmed via Calendly yet. The customer needs to
                        complete their booking using the Calendly link.
                      </p>
                      {selectedAppointment.meeting_link && (
                        <a
                          href={selectedAppointment.meeting_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-2 inline-block text-sm font-medium text-yellow-800 underline hover:text-yellow-900"
                        >
                          Send customer to complete booking →
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-600">Lead Name</label>
                    <p className="text-base font-medium">
                      {selectedAppointment.lead_name || "N/A"}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Email</label>
                    <p className="text-base font-medium">
                      {selectedAppointment.lead_email || "N/A"}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Phone</label>
                    <p className="text-base font-medium">
                      {selectedAppointment.lead_phone || "N/A"}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Status</label>
                    <div className="flex items-center gap-2 mt-1">
                      <span
                        className={`inline-block text-xs font-semibold rounded-full px-3 py-1 ${getStatusColor(
                          selectedAppointment.status
                        )}`}
                      >
                        {selectedAppointment.status.toUpperCase()}
                      </span>
                      {isCalendlyPending(selectedAppointment) ? (
                        <span className="text-xs font-semibold text-yellow-700 bg-yellow-100 px-2 py-1 rounded-full">
                          ⚠️ Pending
                        </span>
                      ) : (
                        <span className="text-xs font-semibold text-green-700 bg-green-100 px-2 py-1 rounded-full">
                          ✓ Confirmed
                        </span>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">
                      Scheduled Time
                    </label>
                    <p className="text-base font-medium">
                      {selectedAppointment.scheduled_at
                        ? new Date(
                            selectedAppointment.scheduled_at
                          ).toLocaleString()
                        : "N/A"}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Duration</label>
                    <p className="text-base font-medium">
                      {selectedAppointment.duration || 30} minutes
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Event ID</label>
                    <p className="text-xs font-mono text-gray-700 bg-gray-100 px-2 py-1 rounded mt-1">
                      {selectedAppointment.calendly_event_id || "N/A"}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Created</label>
                    <p className="text-sm text-gray-700">
                      {selectedAppointment.created_at
                        ? new Date(
                            selectedAppointment.created_at
                          ).toLocaleString()
                        : "N/A"}
                    </p>
                  </div>
                </div>

                {selectedAppointment.meeting_link && (
                  <div>
                    <label className="text-sm text-gray-600">
                      Meeting Link
                    </label>
                    <div className="flex items-center gap-2 mt-1">
                      <a
                        href={selectedAppointment.meeting_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 flex-1 truncate"
                      >
                        {selectedAppointment.meeting_link}
                      </a>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(
                            selectedAppointment.meeting_link
                          );
                          toast.success("Link copied!");
                        }}
                        className="text-gray-600 hover:text-gray-800 text-sm"
                      >
                        📋 Copy
                      </button>
                    </div>
                  </div>
                )}

                {selectedAppointment.notes && (
                  <div>
                    <label className="text-sm text-gray-600">Notes</label>
                    <p className="text-gray-700 bg-gray-50 p-3 rounded mt-1">
                      {selectedAppointment.notes}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => cancelAppointment(selectedAppointment.id)}
                className="btn-secondary btn-md text-red-600"
              >
                Cancel Appointment
              </button>
              <button
                onClick={() => setShowDetailModal(false)}
                className="btn-primary btn-md"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Admin Orders Component - Order management and tracking
const AdminOrders = () => {
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [orderToCancel, setOrderToCancel] = useState(null);
  const [editingOrder, setEditingOrder] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [localFilters, setLocalFilters] = useState({
    status: "",
    paymentStatus: "",
    userId: "",
  });
  const isInitialMount = useRef(true);

  // Use pagination hook
  const {
    data: orders,
    pagination,
    loading,
    setPage,
       setLimit,
    setSearch,
    setFilters,
    searchValue,
    refresh,
  } = usePagination("/api/orders", {
    initialFilters: {},
    autoFetch: true,
  });

  // Update filters when local filters change
  useEffect(() => {
    // Skip on initial mount to prevent double fetch
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const filters = {};
    if (localFilters.status) filters.status = localFilters.status;
    if (localFilters.paymentStatus)
      filters.paymentStatus = localFilters.paymentStatus;
    if (localFilters.userId) filters.userId = localFilters.userId;
    setFilters(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localFilters.status, localFilters.paymentStatus, localFilters.userId]);

  const updateOrderStatus = async (orderId, status, paymentStatus = null) => {
    try {
      const payload = { status };
      if (paymentStatus) {
        payload.paymentStatus = paymentStatus;
      }

      await axios.patch(`/api/orders/${orderId}/status`, payload);
      toast.success("Order updated successfully");
      refresh();

      if (selectedOrder && selectedOrder.id === orderId) {
        const updatedOrder = { ...selectedOrder, status };
        if (paymentStatus) {
          updatedOrder.payment_status = paymentStatus;
        }
        setSelectedOrder(updatedOrder);
      }
    } catch (error) {
      console.error("Error updating order:", error);
      toast.error("Failed to update order");
    }
  };

  const viewOrderDetails = async (orderId) => {
    try {
      const response = await axios.get(`/api/orders/${orderId}`);
      setSelectedOrder(response.data.order);
      setShowDetailModal(true);
    } catch (error) {
      console.error("Error fetching order details:", error);
      toast.error("Failed to load order details");
    }
  };

  const cancelOrder = async (orderId) => {
    setOrderToCancel(orderId);
    setCancelReason("");
    setShowCancelModal(true);
  };

  const confirmCancelOrder = async () => {
    if (!cancelReason.trim()) {
      toast.error("Please provide a cancellation reason");
      return;
    }

    try {
      await axios.patch(`/api/orders/${orderToCancel}/cancel`, {
        cancellation_reason: cancelReason.trim(),
      });
      toast.success("Order cancelled successfully");
      refresh();
      setShowDetailModal(false);
      setShowCancelModal(false);
      setOrderToCancel(null);
      setCancelReason("");
    } catch (error) {
      console.error("Error cancelling order:", error);
      toast.error(error.response?.data?.message || "Failed to cancel order");
    }
  };

  const editOrder = (order) => {
    setEditingOrder(order);
    setEditFormData({
      shipping_address: order.shipping_address || "",
      external_payment_details: order.external_payment_details || "",
      status: order.status,
      payment_status: order.payment_status,
    });
    setShowEditModal(true);
  };

  const saveOrderEdit = async () => {
    if (!editingOrder) return;

    try {
      setSavingEdit(true);
      await axios.patch(`/api/orders/${editingOrder.id}`, editFormData);
      toast.success("Order updated successfully");

      // Refresh orders and update selected order if in detail modal
      refresh();

      if (selectedOrder && selectedOrder.id === editingOrder.id) {
        const response = await axios.get(`/api/orders/${editingOrder.id}`);
        setSelectedOrder(response.data.order);
      }

      setShowEditModal(false);
      setEditingOrder(null);
      setEditFormData({});
    } catch (error) {
      console.error("Error updating order:", error);
      toast.error(error.response?.data?.message || "Failed to update order");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleEditInputChange = (field, value) => {
    setEditFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "processing":
        return "bg-blue-100 text-blue-800";
      case "shipped":
        return "bg-purple-100 text-purple-800";
      case "delivered":
        return "bg-green-100 text-green-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case "paid":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "failed":
        return "bg-red-100 text-red-800";
      case "refunded":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Orders Management</h1>
        <button
          onClick={refresh}
          className="btn-secondary btn-md flex items-center gap-2"
        >
          <span>🔄</span> Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-gray-500">
          <p className="text-sm text-gray-600">Total Orders</p>
          <p className="text-2xl font-bold text-gray-900">
            {pagination?.total || 0}
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-yellow-500">
          <p className="text-sm text-gray-600">Pending</p>
          <p className="text-2xl font-bold text-yellow-600">
            {orders.filter((o) => o.status === "pending").length}
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-blue-500">
          <p className="text-sm text-gray-600">Processing</p>
          <p className="text-2xl font-bold text-blue-600">
            {orders.filter((o) => o.status === "processing").length}
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-purple-500">
          <p className="text-sm text-gray-600">Shipped</p>
          <p className="text-2xl font-bold text-purple-600">
            {orders.filter((o) => o.status === "shipped").length}
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-green-500">
          <p className="text-sm text-gray-600">Delivered</p>
          <p className="text-2xl font-bold text-green-600">
            {orders.filter((o) => o.status === "delivered").length}
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-indigo-500">
          <p className="text-sm text-gray-600">Revenue</p>
          <p className="text-2xl font-bold text-indigo-600">
            ₹
            {orders
              .reduce((sum, o) => sum + parseFloat(o.total_amount || 0), 0)
              .toFixed(2)}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Order Status
            </label>
            <select
              value={localFilters.status}
              onChange={(e) =>
                setLocalFilters({ ...localFilters, status: e.target.value })
              }
              className="input"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="shipped">Shipped</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Payment Status
            </label>
            <select
              value={localFilters.paymentStatus}
              onChange={(e) =>
                setLocalFilters({
                  ...localFilters,
                  paymentStatus: e.target.value,
                })
              }
              className="input"
            >
              <option value="">All Payment Statuses</option>
              <option value="pending">Pending</option>
              <option value="paid">Paid</option>
              <option value="failed">Failed</option>
              <option value="refunded">Refunded</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Specific Customer ID
            </label>
            <input
              type="number"
              value={localFilters.userId}
              onChange={(e) =>
                setLocalFilters({ ...localFilters, userId: e.target.value })
              }
              placeholder="Exact user ID..."
              className="input"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={() =>
                setFilters({
                  status: "",
                  paymentStatus: "",
                  userId: "",
                  customerSearch: "",
                })
              }
              className="btn-secondary btn-md w-full"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="loading-dots">
            <div></div>
            <div></div>
            <div></div>
            <div></div>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {orders.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No orders found matching the filters.
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Order ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Total Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Order Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Payment Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {orders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        #{order.id}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {order.customer_name || "Guest"}
                      </div>
                      <div className="text-sm text-gray-500">
                        {order.customer_email || "N/A"}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-bold text-gray-900">
                        ₹{parseFloat(order.total_amount).toFixed(2)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <select
                        value={order.status}
                        onChange={(e) =>
                          updateOrderStatus(order.id, e.target.value)
                        }
                        className={`text-xs font-semibold rounded-full px-3 py-1 ${getStatusColor(
                          order.status
                        )}`}
                      >
                        <option value="pending">Pending</option>
                        <option value="processing">Processing</option>
                        <option value="shipped">Shipped</option>
                        <option value="delivered">Delivered</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <select
                        value={order.payment_status}
                        onChange={(e) =>
                          updateOrderStatus(
                            order.id,
                            order.status,
                            e.target.value
                          )
                        }
                        className={`text-xs font-semibold rounded-full px-3 py-1 ${getPaymentStatusColor(
                          order.payment_status
                        )}`}
                      >
                        <option value="pending">Pending</option>
                        <option value="paid">Paid</option>
                        <option value="failed">Failed</option>
                        <option value="refunded">Refunded</option>
                      </select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(order.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                      <button
                        onClick={() => viewOrderDetails(order.id)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        View
                      </button>
                      {order.status !== "delivered" &&
                        order.status !== "cancelled" && (
                          <button
                            onClick={() => cancelOrder(order.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Cancel
                          </button>
                        )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Pagination Component */}
          <div className="px-6 py-4 border-t border-gray-200">
            <Pagination
              pagination={pagination}
              onPageChange={setPage}
              onLimitChange={setLimit}
              onSearchChange={setSearch}
              searchValue={searchValue}
              searchPlaceholder="Search by customer name, email, order ID..."
              showSearch={true}
              showLimitSelector={true}
              limitOptions={[10, 20, 50, 100]}
            />
          </div>
        </div>
      )}

      {/* Order Detail Modal */}
      {showDetailModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">
                  Order Details - #{selectedOrder.id}
                </h2>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="text-2xl">×</span>
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* Order Info */}
              <div className="grid grid-cols-2 gap-6 mb-6">
                <div>
                  <h3 className="text-lg font-semibold mb-3">
                    Order Information
                  </h3>
                  <div className="space-y-2">
                    <div>
                      <label className="text-sm text-gray-600">Order ID</label>
                      <p className="text-base font-medium">
                        #{selectedOrder.id}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">
                        Order Date
                      </label>
                      <p className="text-base font-medium">
                        {new Date(selectedOrder.created_at).toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">
                        Order Status
                      </label>
                      <p
                        className={`inline-block text-xs font-semibold rounded-full px-3 py-1 mt-1 ${getStatusColor(
                          selectedOrder.status
                        )}`}
                      >
                        {selectedOrder.status.toUpperCase()}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">
                        Payment Status
                      </label>
                      <p
                        className={`inline-block text-xs font-semibold rounded-full px-3 py-1 mt-1 ${getPaymentStatusColor(
                          selectedOrder.payment_status
                        )}`}
                      >
                        {selectedOrder.payment_status.toUpperCase()}
                      </p>
                    </div>
                    {selectedOrder.status === "cancelled" &&
                      selectedOrder.cancellation_reason && (
                        <div>
                          <label className="text-sm text-gray-600">
                            Cancellation Reason
                          </label>
                          <p className="text-sm text-red-700 bg-red-50 p-2 rounded mt-1 border border-red-200">
                            {selectedOrder.cancellation_reason}
                          </p>
                        </div>
                      )}
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">
                    Customer Information
                  </h3>
                  <div className="space-y-2">
                    <div>
                      <label className="text-sm text-gray-600">Name</label>
                      <p className="text-base font-medium">
                        {selectedOrder.customer_name || "Guest Customer"}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">Email</label>
                      <p className="text-base font-medium">
                        {selectedOrder.customer_email || "N/A"}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">Phone</label>
                      <p className="text-base font-medium">
                        {selectedOrder.customer_phone || "N/A"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Shipping Address */}
              {selectedOrder.shipping_address && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-2">
                    Shipping Address
                  </h3>
                  <p className="text-gray-700 bg-gray-50 p-3 rounded">
                    {selectedOrder.shipping_address}
                  </p>
                </div>
              )}

              {/* External Payment Details */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-3">
                  External Payment Details
                </h3>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  {selectedOrder.external_payment_details ? (
                    <div className="mb-4">
                      <label className="text-sm text-gray-600 font-medium">
                        Payment Details
                      </label>
                      <p className="text-gray-700 bg-white p-3 rounded mt-1 border">
                        {selectedOrder.external_payment_details}
                      </p>
                    </div>
                  ) : (
                    <div className="mb-4">
                      <label className="text-sm text-gray-600 font-medium">
                        Payment Details
                      </label>
                      <p className="text-gray-500 bg-gray-100 p-3 rounded mt-1 border italic">
                        No external payment details provided
                      </p>
                    </div>
                  )}

                  {selectedOrder.payment_image ? (
                    <div>
                      <label className="text-sm text-gray-600 font-medium">
                        Payment Proof Image
                      </label>
                      <div className="mt-2">
                        <img
                          src={selectedOrder.payment_image}
                          alt="Payment Proof"
                          className="max-w-md max-h-64 rounded-lg border shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                          onClick={() =>
                            window.open(
                              `https://tamilsandhai.in${selectedOrder.payment_image}`,
                              "_blank"
                            )
                          }
                          onError={(e) => {
                            console.error(
                              "Image load error for:",
                              selectedOrder.payment_image
                            );
                            console.error(
                              "Full URL attempted:",
                              selectedOrder.payment_image
                            );
                            console.error("Error event:", e);
                            e.target.style.display = "none";
                            if (e.target.nextElementSibling) {
                              e.target.nextElementSibling.style.display =
                                "block";
                            }
                          }}
                          onLoad={(e) => {
                            console.log(
                              "Image loaded successfully:",
                              selectedOrder.payment_image
                            );
                            console.log(
                              "Image dimensions:",
                              e.target.naturalWidth,
                              "x",
                              e.target.naturalHeight
                            );
                          }}
                        />
                        <div
                          className="bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center"
                          style={{ display: "none" }}
                        >
                          <div className="text-gray-500">
                            <svg
                              className="mx-auto h-12 w-12 text-gray-400"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v14a2 2 0 002 2z"
                              />
                            </svg>
                            <p className="mt-2 text-sm text-gray-500">
                              Payment image could not be loaded
                            </p>
                            <p className="text-xs text-gray-400">
                              File: {selectedOrder.payment_image}
                            </p>
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Click to view full size
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <label className="text-sm text-gray-600 font-medium">
                        Payment Proof Image
                      </label>
                      <p className="text-gray-500 bg-gray-100 p-3 rounded mt-1 border italic">
                        No payment proof image uploaded
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Order Items */}
              {selectedOrder.items && selectedOrder.items.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-3">Order Items</h3>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Product
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Price
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Quantity
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Subtotal
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {selectedOrder.items.map((item) => (
                          <tr key={item.id}>
                            <td className="px-4 py-4">
                              <div className="flex items-center">
                                {item.product_image && (
                                  <img
                                    src={item.product_image}
                                    alt={item.product_name}
                                    className="h-10 w-10 rounded object-cover mr-3"
                                    onError={(e) => {
                                      e.target.src =
                                        "https://via.placeholder.com/40";
                                    }}
                                  />
                                )}
                                <div>
                                  <p className="text-sm font-medium text-gray-900">
                                    {item.product_name ||
                                      `Product #${item.product_id}`}
                                  </p>
                                  {item.product_description && (
                                    <p className="text-xs text-gray-500 line-clamp-1">
                                      {item.product_description}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                              ₹{parseFloat(item.price).toFixed(2)}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                              {item.quantity}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              ₹
                              {(parseFloat(item.price) * item.quantity).toFixed(
                                2
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-gray-50">
                        <tr>
                          <td
                            colSpan="3"
                            className="px-4 py-4 text-right text-sm font-semibold text-gray-900"
                          >
                            Total Amount:
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                            ₹{parseFloat(selectedOrder.total_amount).toFixed(2)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => editOrder(selectedOrder)}
                className="btn-secondary btn-md text-blue-600"
              >
                Edit Order
              </button>
              {selectedOrder.status !== "delivered" &&
                selectedOrder.status !== "cancelled" && (
                  <button
                    onClick={() => cancelOrder(selectedOrder.id)}
                    className="btn-secondary btn-md text-red-600"
                  >
                    Cancel Order
                  </button>
                )}
              <button
                onClick={() => setShowDetailModal(false)}
                className="btn-primary btn-md"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Order Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-900">
                  Cancel Order #{orderToCancel}
                </h2>
                <button
                  onClick={() => setShowCancelModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="text-2xl">×</span>
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  Please provide a reason for canceling this order. This will be
                  shown to the customer.
                </p>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-yellow-400"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-yellow-700">
                        <strong>Important:</strong> The cancellation reason will
                        be visible to the customer in their order details. Be
                        respectful and professional.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cancellation Reason *
                </label>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="e.g., Out of stock, Customer request, Payment issue, etc."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  rows={4}
                  maxLength={500}
                />
                <div className="flex justify-between mt-1">
                  <p className="text-xs text-gray-500">
                    Be specific and professional as customers will see this
                    reason.
                  </p>
                  <p className="text-xs text-gray-500">
                    {cancelReason.length}/500
                  </p>
                </div>
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  <strong>Common reasons:</strong>
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {[
                    "Out of stock",
                    "Product discontinued",
                    "Customer request",
                    "Payment verification failed",
                    "Unable to deliver to location",
                    "Technical issue",
                  ].map((reason) => (
                    <button
                      key={reason}
                      onClick={() => setCancelReason(reason)}
                      className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors"
                    >
                      {reason}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowCancelModal(false)}
                className="btn-secondary btn-md"
              >
                Keep Order
              </button>
              <button
                onClick={confirmCancelOrder}
                disabled={!cancelReason.trim()}
                className="btn-primary btn-md bg-red-600 hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Cancel Order
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Order Modal */}
      {showEditModal && editingOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-900">
                  Edit Order #{editingOrder.id}
                </h2>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="text-2xl">×</span>
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-blue-400"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-blue-700">
                      <strong>Note:</strong> Be careful when editing order
                      details. Changes will be reflected in customer views and
                      reports.
                    </p>
                  </div>
                </div>
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  saveOrderEdit();
                }}
                className="space-y-6"
              >
                {/* Order Status */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Order Status
                    </label>
                    <select
                      value={editFormData.status}
                      onChange={(e) =>
                        handleEditInputChange("status", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="pending">Pending</option>
                      <option value="processing">Processing</option>
                      <option value="shipped">Shipped</option>
                      <option value="delivered">Delivered</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Payment Status
                    </label>
                    <select
                      value={editFormData.payment_status}
                      onChange={(e) =>
                        handleEditInputChange("payment_status", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="pending">Pending</option>
                      <option value="paid">Paid</option>
                      <option value="failed">Failed</option>
                      <option value="refunded">Refunded</option>
                    </select>
                  </div>
                </div>

                {/* Shipping Address */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Shipping Address
                  </label>
                  <textarea
                    value={editFormData.shipping_address}
                    onChange={(e) =>
                      handleEditInputChange("shipping_address", e.target.value)
                    }
                    placeholder="Enter complete shipping address..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    rows={3}
                  />
                </div>

                {/* External Payment Details */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    External Payment Details
                  </label>
                  <textarea
                    value={editFormData.external_payment_details}
                    onChange={(e) =>
                      handleEditInputChange(
                        "external_payment_details",
                        e.target.value
                      )
                    }
                    placeholder="Enter external payment details (bank transfer, UPI, etc.)..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    rows={3}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    This information will be visible to the customer in their
                    order details.
                  </p>
                </div>

                {/* Warning for sensitive changes */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-yellow-400"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-yellow-700">
                        <strong>Important:</strong> Changing order status may
                        affect inventory and customer notifications. Ensure all
                        changes are accurate.
                      </p>
                    </div>
                  </div>
                </div>
              </form>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowEditModal(false)}
                className="btn-secondary btn-md"
                disabled={savingEdit}
              >
                Cancel
              </button>
              <button
                onClick={saveOrderEdit}
                disabled={savingEdit}
                className="btn-primary btn-md bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {savingEdit ? (
                  <div className="flex items-center">
                    <div className="loading-dots mr-2">
                      <div></div>
                      <div></div>
                      <div></div>
                    </div>
                    Saving...
                  </div>
                ) : (
                  "Save Changes"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AdminProducts = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageInputMode, setImageInputMode] = useState("url"); // 'url' or 'upload'
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    image_url: "",
    payment_link: "",
    category: "",
    stock_quantity: "",
    status: "active",
  });

  // Use pagination hook
  const {
    data: products,
    pagination,
    loading,
    setPage,
    setLimit,
    setSearch,
    searchValue,
    refresh,
  } = usePagination("/api/products", {
    initialFilters: { status: "active" },
  });

  // Default placeholder image as inline SVG to avoid network errors
  const defaultProductImage =
    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='50' height='50' viewBox='0 0 50 50'%3E%3Crect fill='%23f3f4f6' width='50' height='50'/%3E%3Ctext fill='%236b7280' font-family='Arial, sans-serif' font-size='10' x='50%25' y='50%25' text-anchor='middle' dy='.3em'%3ENo Image%3C/text%3E%3C/svg%3E";

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProduct) {
        await axios.put(`/api/products/${editingProduct.id}`, formData);
        toast.success("Product updated successfully!");
      } else {
        await axios.post("/api/products", formData);
        toast.success("Product created successfully!");
      }
      setShowForm(false);
      setEditingProduct(null);
      resetForm();
      refresh();
    } catch (error) {
      console.error("Error saving product:", error);
      toast.error(error.response?.data?.message || "Failed to save product");
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description || "",
      price: product.price,
      image_url: product.image_url || "",
      payment_link: product.payment_link || "",
      category: product.category || "",
      stock_quantity: product.stock_quantity || "",
      status: product.status,
    });
    setShowForm(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm("Are you sure you want to delete this product?")) {
      return;
    }
    try {
      await axios.delete(`/api/products/${productId}`);
      toast.success("Product deleted successfully!");
      refresh();
    } catch (error) {
      console.error("Error deleting product:", error);
      toast.error("Failed to delete product");
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/webp",
      "image/gif",
    ];
    if (!validTypes.includes(file.type)) {
      toast.error("Please upload a valid image file (JPG, PNG, WEBP, or GIF)");
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast.error("Image size should be less than 5MB");
      return;
    }

    setUploadingImage(true);

    try {
      // Convert to base64 data URL for storage
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setFormData({ ...formData, image_url: base64String });
        toast.success("Image uploaded successfully!");
        setUploadingImage(false);
      };
      reader.onerror = () => {
        toast.error("Failed to read image file");
        setUploadingImage(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Failed to upload image");
      setUploadingImage(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      price: "",
      image_url: "",
      payment_link: "",
      category: "",
      stock_quantity: "",
      status: "active",
    });
    setImageInputMode("url");
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          Products Management
        </h1>
        <button
          onClick={() => {
            setShowForm(!showForm);
            setEditingProduct(null);
            resetForm();
          }}
          className="btn-primary btn-md"
        >
          {showForm ? "Cancel" : "+ Add Product"}
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">
            {editingProduct ? "Edit Product" : "Add New Product"}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Product Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="input"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Price *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) =>
                    setFormData({ ...formData, price: e.target.value })
                  }
                  className="input"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                className="input"
                rows="3"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) =>
                    setFormData({ ...formData, category: e.target.value })
                  }
                  className="input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Stock Quantity
                </label>
                <input
                  type="number"
                  value={formData.stock_quantity}
                  onChange={(e) =>
                    setFormData({ ...formData, stock_quantity: e.target.value })
                  }
                  className="input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Product Image
              </label>

              {/* Image Input Mode Toggle */}
              <div className="flex gap-2 mb-3">
                {/* <button
                  type="button"
                  onClick={() => setImageInputMode("url")}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    imageInputMode === "url"
                      ? "bg-primary-600 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  🔗 Enter URL
                </button> */}
                <button
                  type="button"
                  onClick={() => setImageInputMode("upload")}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    imageInputMode === "upload"
                      ? "bg-primary-600 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  📤 Upload Image
                </button>
              </div>

              {/* URL Input Mode */}
              {imageInputMode === "url" && (
                <div>
                  <input
                    type="url"
                    value={formData.image_url}
                    onChange={(e) =>
                      setFormData({ ...formData, image_url: e.target.value })
                    }
                    className="input"
                    placeholder="https://example.com/image.jpg"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    ⚠️ Must be a direct image URL (ending in .jpg, .png, .webp,
                    etc.)
                  </p>
                </div>
              )}

              {/* Upload Input Mode */}
              {imageInputMode === "upload" && (
                <div>
                  <div className="flex items-center justify-center w-full">
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <svg
                          className="w-8 h-8 mb-2 text-gray-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                          />
                        </svg>
                        <p className="mb-1 text-sm text-gray-500">
                          <span className="font-semibold">Click to upload</span>{" "}
                          or drag and drop
                        </p>
                        <p className="text-xs text-gray-500">
                          PNG, JPG, WEBP or GIF (MAX. 5MB)
                        </p>
                      </div>
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={handleImageUpload}
                        disabled={uploadingImage}
                      />
                    </label>
                  </div>
                  {uploadingImage && (
                    <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
                      <div className="loading-dots">
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                      </div>
                      Uploading...
                    </div>
                  )}
                </div>
              )}

              {/* Image Preview (shows for both modes) */}
              {formData.image_url && !uploadingImage && (
                <div className="mt-3">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs font-medium text-gray-700">
                      Preview:
                    </p>
                    <button
                      type="button"
                      onClick={() =>
                        setFormData({ ...formData, image_url: "" })
                      }
                      className="text-xs text-red-600 hover:text-red-800"
                    >
                      Remove Image
                    </button>
                  </div>
                  <div className="relative inline-block">
                    <img
                      src={formData.image_url}
                      alt="Preview"
                      className="h-32 w-32 object-cover rounded border-2 border-gray-300"
                      onError={(e) => {
                        e.target.style.display = "none";
                        e.target.nextElementSibling.style.display = "block";
                      }}
                      onLoad={(e) => {
                        e.target.style.display = "block";
                        if (e.target.nextElementSibling) {
                          e.target.nextElementSibling.style.display = "none";
                        }
                      }}
                    />
                    <div className="hidden p-4 bg-red-50 border border-red-200 rounded text-xs text-red-800 max-w-sm">
                      ❌ Invalid image URL. The URL should point directly to an
                      image file.
                      <br />
                      <strong>Examples:</strong>
                      <br />
                      ✅ https://example.com/product.jpg
                      <br />
                      ✅ https://cdn.example.com/images/item.png
                      <br />❌ https://example.com/product-page (webpage, not
                      image)
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                External Payment Link
              </label>
              <input
                type="url"
                value={formData.payment_link}
                onChange={(e) =>
                  setFormData({ ...formData, payment_link: e.target.value })
                }
                className="input"
                placeholder="https://payment-gateway.com/checkout/..."
              />
              <p className="text-xs text-gray-500 mt-1">
                Optional: Add external payment link (Stripe, PayPal, etc.)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) =>
                  setFormData({ ...formData, status: e.target.value })
                }
                className="input"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div className="flex gap-3">
              <button type="submit" className="btn-primary btn-md">
                {editingProduct ? "Update Product" : "Create Product"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingProduct(null);
                  resetForm();
                }}
                className="btn-secondary btn-md"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="loading-dots">
            <div></div>
            <div></div>
            <div></div>
            <div></div>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Product
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Stock
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Payment Link
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {products.map((product) => (
                <tr key={product.id}>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <img
                        src={product.image_url || defaultProductImage}
                        alt={product.name}
                        className="h-10 w-10 rounded object-cover"
                        onError={(e) => {
                          // Prevent infinite loop by checking if already set to default
                          if (e.target.src !== defaultProductImage) {
                            e.target.src = defaultProductImage;
                          }
                        }}
                      />
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {product.name}
                        </div>
                        <div className="text-sm text-gray-500 line-clamp-1">
                          {product.description}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    ₹{product.price}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {product.category || "N/A"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {product.stock_quantity}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {product.payment_link ? (
                      <span className="text-green-600">✓ Set</span>
                    ) : (
                      <span className="text-gray-400">Not set</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                    <button
                      onClick={() => handleEdit(product)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(product.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination Component */}
          <div className="px-6 py-4 border-t border-gray-200">
            <Pagination
              pagination={pagination}
              onPageChange={setPage}
              onLimitChange={setLimit}
              onSearchChange={setSearch}
              searchValue={searchValue}
              searchPlaceholder="Search by product name, category..."
              showSearch={true}
              showLimitSelector={true}
              limitOptions={[10, 20, 50, 100]}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const AdminCommunications = () => (
  <div>
    <h1 className="text-2xl font-bold text-gray-900 mb-6">
      Communications Management
    </h1>
    <div className="bg-white rounded-lg shadow-sm p-6">
      <p className="text-gray-600">
        Communications management interface will be implemented here.
      </p>
      <p className="text-sm text-gray-500 mt-2">
        This will show all email and WhatsApp communications with delivery
        status.
      </p>
    </div>
  </div>
);

const AdminTasks = () => {
  const [users, setUsers] = useState([]);
  const [filterUserId, setFilterUserId] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const isInitialMount = useRef(true);

  // Use pagination hook
  const {
    data: tasks,
    pagination,
    loading,
    setPage,
    setLimit,
    setSearch,
    setFilters,
    searchValue,
    refresh,
  } = usePagination("/api/tasks/all", {
    initialFilters: {},
    autoFetch: true,
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  // Update filters when filter values change
  useEffect(() => {
    // Skip on initial mount to prevent double fetch
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const filters = {};
    if (filterUserId) filters.userId = filterUserId;
    if (filterStatus) filters.status = filterStatus;
    setFilters(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterUserId, filterStatus]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get("/api/tasks/users");
      setUsers(response.data.users || []);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const updateTaskStatus = async (taskId, newStatus) => {
    try {
      await axios.put(`/api/tasks/${taskId}`, { status: newStatus });
      toast.success("Task status updated");
      refresh();
    } catch (error) {
      console.error("Error updating task:", error);
      toast.error("Failed to update task");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "bg-green-100 text-green-800";
      case "in_progress":
        return "bg-blue-100 text-blue-800";
      case "blocked":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high":
        return "text-red-600";
      case "medium":
        return "text-yellow-600";
      default:
        return "text-gray-600";
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">All User Tasks</h1>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Filter by User
            </label>
            <select
              value={filterUserId}
              onChange={(e) => setFilterUserId(e.target.value)}
              className="input"
            >
              <option value="">All Users</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name} ({user.role})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Filter by Status
            </label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="input"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="blocked">Blocked</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setFilterUserId("");
                setFilterStatus("");
              }}
              className="btn-secondary btn-md w-full"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Tasks Table */}
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="loading-dots">
            <div></div>
            <div></div>
            <div></div>
            <div></div>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {tasks.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No tasks found matching the filters.
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Task Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Priority
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {tasks.map((task) => (
                  <tr key={task.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {task.user_name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {task.user_role}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(task.task_date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div className="max-w-md">
                        {task.task_description}
                        {task.notes && (
                          <div className="text-xs text-gray-500 mt-1">
                            Note: {task.notes}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`text-sm font-medium ${getPriorityColor(
                          task.priority
                        )}`}
                      >
                        {task.priority.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <select
                        value={task.status}
                        onChange={(e) =>
                          updateTaskStatus(task.id, e.target.value)
                        }
                        className={`text-xs font-semibold rounded-full px-2 py-1 ${getStatusColor(
                          task.status
                        )}`}
                      >
                        <option value="pending">PENDING</option>
                        <option value="in_progress">IN PROGRESS</option>
                        <option value="completed">COMPLETED</option>
                        <option value="blocked">BLOCKED</option>
                      </select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className="text-gray-500">
                        {new Date(task.created_at).toLocaleString()}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Pagination Component */}
          <div className="px-6 py-4 border-t border-gray-200">
            <Pagination
              pagination={pagination}
              onPageChange={setPage}
              onLimitChange={setLimit}
              onSearchChange={setSearch}
              searchValue={searchValue}
              searchPlaceholder="Search by task title, user..."
              showSearch={true}
              showLimitSelector={true}
              limitOptions={[10, 20, 50, 100]}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
